import React, { createContext } from 'react'
import { useKeycloak } from '@react-keycloak/web'
import { useIntl } from 'react-intl'
import { NamedRoutes } from './routes'
import { useHistory } from 'react-router-dom'
import SubscriptionServices from './Services/Consumers/SubscriptionServices'
import { PlanPayload } from './Services/Payloads/AuthenticatedUserPayload'

const PlanContext = createContext({})

const PlanProvider = ({ children, pageAction, pid, handleNotificationCall }) => {
  const { keycloak } = useKeycloak()
  const intl = useIntl()
  const history = useHistory()
  const [deletePlanLoading, setDeletePlanLoading] = React.useState(false)
  const [planDataLoading, setPlanDataLoading] = React.useState(false)
  const [planDataFailure, setPlanDataFailure] = React.useState(false)
  const [planData, setPlanData] = React.useState({})

  const [servicesList, setServicesList] = React.useState([])
  const [servicesListLoading, setServicesListLoading] = React.useState(false)

  React.useEffect(() => {
      if ('delete' !== pageAction) {
        getServicesList()
      }
      if (!!pid && !!parseInt(pid)) {
        getPlanData()
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const getServicesList = () => {
    if (!!keycloak?.authenticated && !servicesList.length) {
      setServicesListLoading(true)
      SubscriptionServices.getServices({
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
        params: { 'page': 0, 'limit': 0 }
      }).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
          handleNotificationCall({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            setServicesList(result.data.data)
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
              color: 'error'
            }, 'error')
          }
        }
      }).then(() => {
        setServicesListLoading(false)
      })
    }
  }

  const getPlanData = () => {
    if (!!keycloak?.authenticated) {
      setPlanDataFailure(false)
      setPlanDataLoading(true)
      SubscriptionServices.getPlanData(pid, { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } }).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          setPlanDataFailure(true)
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            let servicesIds = (!!result.data.data.services && !!result.data.data.services.length) ?
              result.data.data.services.map(service => service.id) : []
            setPlanData({ ...result.data.data, 'servicesIds': servicesIds })
          } else {
            setPlanDataFailure(true)
          }
        }
      }).then(() => {
        setPlanDataLoading(false)
      })
    }
  }

  const handlePlanSubmit = (values, FormikActions) => {
    if (!!keycloak?.authenticated) {
      FormikActions.setSubmitting(true)
      SubscriptionServices.submitPlan(pid, pageAction === 'edit', PlanPayload(values), {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }, FormikActions.setSubmitting, null, FormikActions).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
          handleNotificationCall({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            handleNotificationCall({
              type: 'done',
              message: intl.formatMessage({ id: pageAction === 'edit' ? 'Plan.Edit.FormSuccessAlert' : 'Plan.Add.FormSuccessAlert' }),
              color: 'success'
            }, 'success')
            history.push(NamedRoutes.subscription.plan.list)
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
              color: 'error'
            }, 'error')
          }
        }
      }).then(() => {
        FormikActions.setSubmitting(false)
      })
    }
  }

  const handleDeletePlan = (index, data, setData, count, setCount, handleDeletePlanDialog) => {
    if (!!keycloak?.authenticated) {
      setDeletePlanLoading(true)
      SubscriptionServices.deletePlan(data[index].id, {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }, setDeletePlanLoading).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          handleNotificationCall({
            type: 'defence',
            message: result.response?.data?.status?.errors || intl.formatMessage({id: 'BasicInfo.FormErrorAlert'}),
            color: 'error'
          }, 'error')
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            handleNotificationCall({
              type: 'done',
              message: intl.formatMessage({ id: 'Plan.Delete.FormSuccessAlert' }),
              color: 'success'
            }, 'success')
            let newData = data
            newData.splice(index, 1)
            setData(newData)
            setCount(--count)
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
              color: 'error'
            }, 'error')
          }
        }
      }).then(() => {
        setDeletePlanLoading(false)
        handleDeletePlanDialog()
      })
    }
  }

  return (
    <PlanContext.Provider value={{
      planDataLoading: planDataLoading,
      planDataFailure: planDataFailure,
      planData: planData,
      pageAction: pageAction,

      servicesListLoading: servicesListLoading,
      servicesList: servicesList,
      handlePlanSubmit,

      deletePlanLoading: deletePlanLoading,
      handleDeletePlan
    }}>
      {children}
    </PlanContext.Provider>
  )
}
const PlanConsumer = PlanContext.Consumer

export { PlanProvider, PlanConsumer }
